/* global setInterval, clearInterval */

/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * ShareIntentHost.js
 *
 * Receives "Share to Happy" from the OS and opens the share screen.
 *
 * Native side: `expo-share-intent` — Android `SEND` / `SEND_MULTIPLE` intent
 * filters on MainActivity (android/app/src/main/AndroidManifest.xml) and the
 * iOS "Happy" share extension (generated from the `expo-share-intent` plugin
 * entry in app.json). The extension stores the shared items in the App Group
 * and opens `happy-app://dataUrl=…`; the hook reads them back. Shared files are
 * local copies (Android cache dir / iOS App Group container).
 *
 * Flow:
 *  1. the hook reports a share → it is normalized (`sharePayload`) and parked in
 *     `shareInbox`, then the native intent is reset so it never fires twice;
 *  2. once the user is logged in and the navigator is ready, the host navigates
 *     to `ShareToHappyScreen` with the share's id — and keeps re-trying for a few
 *     seconds until the screen CLAIMS it, because the navigator is re-keyed
 *     (remounted) on login and an early navigation would be lost;
 *  3. logged out → a toast asks to sign in; the share waits in the inbox.
 *
 * Fail-soft: on web / Tauri, or a binary built before the native module shipped,
 * `requireOptionalNativeModule` yields null and the host renders nothing.
 *
 * @module components/share/ShareIntentHost
 */

import React, {useEffect, useRef, useState} from 'react';
import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import {useTranslation} from 'react-i18next';

import {navigationRef} from '../../helpers/navigationRef';
import {normalizeShareIntent} from '../../helpers/share/sharePayload';
import {getPendingShare, setPendingShare, subscribePendingShare} from '../../helpers/share/shareInbox';
import {discardShareFiles} from '../../helpers/share/shareActions';

/** Screen the host routes every share to. */
export const SHARE_SCREEN_NAME = 'ShareToHappyScreen';

/** Retry cadence / budget while waiting for the screen to claim the share. */
const NAV_RETRY_MS = 500;
const NAV_RETRY_MAX = 30;

/**
 * Load the native share-intent bindings, or null where they cannot exist.
 * @returns {?{useShareIntent: Function}}
 */
const loadShareIntentLib = () => {
    if (Platform.OS === 'web') {
        return null;
    }
    try {
        // eslint-disable-next-line global-require
        const lib = require('expo-share-intent');

        return typeof lib?.useShareIntent === 'function' ? lib : null;
    } catch (_error) {
        return null;
    }
};

const shareIntentLib = loadShareIntentLib();

/**
 * Subscribe to the pending share in `shareInbox`.
 * @returns {?object}
 */
const usePendingShare = () => {
    const [share, setShare] = useState(getPendingShare());

    useEffect(() => subscribePendingShare(setShare), []);

    return share;
};

/**
 * Navigate to the share screen for `shareId` unless it is already showing it.
 * @param {string} shareId Share id.
 * @returns {boolean} Whether the screen is (now) on top for that share.
 */
const presentShare = (shareId) => {
    try {
        if (!navigationRef.isReady()) {
            return false;
        }

        const current = navigationRef.getCurrentRoute();

        if (current?.name === SHARE_SCREEN_NAME && current?.params?.shareId === shareId) {
            return true;
        }
        navigationRef.navigate(SHARE_SCREEN_NAME, {shareId});

        return true;
    } catch (_error) {
        // Screen not registered in this navigator yet (pre-login stack) — retry.
        return false;
    }
};

/**
 * @param {object} props
 * @param {boolean} props.isLoggedIn Whether a session exists.
 * @returns {null}
 */
const NativeShareIntentListener = ({isLoggedIn}) => {
    const {t} = useTranslation();
    const {hasShareIntent, shareIntent, resetShareIntent} = shareIntentLib.useShareIntent({
        // The payload is copied into shareInbox at once, so a trip to the
        // background must not wipe it (the user may switch apps mid-flow).
        resetOnBackground: false,
    });
    const pending = usePendingShare();
    const toastedRef = useRef(null);

    useEffect(() => {
        if (!hasShareIntent) {
            return;
        }

        const payload = normalizeShareIntent(shareIntent);

        if (payload) {
            const previous = getPendingShare();

            // A newer share replaces one the screen never opened: its temp copies
            // would otherwise stay on disk forever. (A claimed one is the screen's
            // to clean up — it may be uploading it right now.)
            if (previous && !previous.claimed) {
                discardShareFiles(previous.items).catch(() => {});
            }
            setPendingShare(payload);
        }
        resetShareIntent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasShareIntent, shareIntent]);

    useEffect(() => {
        if (!pending || pending.claimed) {
            return undefined;
        }
        if (!isLoggedIn) {
            if (toastedRef.current !== pending.id) {
                toastedRef.current = pending.id;
                Toast.show({
                    type: 'info',
                    text1: t('share_login_required_title', {defaultValue: 'Jelentkezz be a megosztáshoz'}),
                    text2: t('share_login_required_text', {defaultValue: 'Bejelentkezés után folytatjuk a megosztást.'}),
                });
            }

            return undefined;
        }

        let attempts = 0;
        const timer = setInterval(() => {
            attempts += 1;
            // Keep nudging until the screen claims the share (claim → `pending.claimed`
            // re-runs this effect and clears the timer) or the budget runs out.
            presentShare(pending.id);
            if (attempts >= NAV_RETRY_MAX) {
                clearInterval(timer);
            }
        }, NAV_RETRY_MS);

        presentShare(pending.id);

        return () => clearInterval(timer);
    }, [pending, isLoggedIn, t]);

    return null;
};

/**
 * Mount once, inside the providers (after the navigator), on native only.
 * @param {object} props
 * @param {boolean} props.isLoggedIn Whether a session exists.
 * @returns {?JSX.Element}
 */
const ShareIntentHost = ({isLoggedIn}) => (shareIntentLib ? <NativeShareIntentListener isLoggedIn={!!isLoggedIn} /> : null);

export default ShareIntentHost;
